/**
 * positionEligibility — shared frontend helper for slot eligibility checks.
 *
 * Layer 1 of the 6-layer eligibility invariant (Tony 2026-05-13 dispatch).
 * Mirrors the backend canonical compute_effective_eligibility helper at
 * core/position_utils.py by reading the pre-computed roster.effective_eligibility
 * field that the API populates via player_service._inject_effective_eligibility.
 *
 * Bible § 4.5.9: ONE source of truth for "what slots can this player fill?"
 * Bible § 14: shape discipline — read canonical, no flat fallbacks.
 *
 * DO NOT re-implement composite-slot expansion here. The backend's canonical
 * helper already composes default + auto-earned (current OR prior season) +
 * custom override. The frontend just reads roster.effective_eligibility and
 * checks membership.
 *
 * Established 2026-05-13 in response to the JJ Wetherholt → SS_0 eligibility
 * bypass investigation. Replaces ad-hoc isEligibleForPosition implementations
 * scattered across position-assignment surfaces.
 */

/**
 * Strip a slot index suffix.
 *   'SS_0' → 'SS'
 *   'OF_2' → 'OF'
 *   'P_5'  → 'P'
 *   'BENCH' / null → as-is
 */
export const slotTypeFromPosition = (position) => {
  if (!position) return '';
  if (!position.includes('_')) return position;
  return position.split('_', 1)[0];
};

/**
 * Read the player's canonical effective eligibility list.
 *
 * Per Bible Rule 9: returns null if the canonical field is missing, signaling
 * the caller that data integrity is wrong. Callers MUST decide explicitly how
 * to handle (typically: surface error, disable UI). NO silent fallback to
 * primary-position-only, which would skip composite-slot expansion and
 * auto-earned positions.
 *
 * @param {Object} player Canonical player object
 * @returns {string[]|null} Sorted array of eligible slot codes, or null if
 *                          the canonical field is missing.
 */
export const getEffectiveEligibility = (player) => {
  const effective = player?.league_context?.roster?.effective_eligibility;
  if (!Array.isArray(effective)) return null;
  return effective;
};

/**
 * Is the player eligible to occupy the given slot?
 *
 * @param {Object} player Canonical player object
 * @param {string} slot   Roster slot id (e.g., 'SS_0', 'OF_2'). 'BENCH' / falsy
 *                        always returns true.
 * @returns {boolean}
 *
 * Returns false if effective_eligibility is missing (per Bible Rule 9 — no
 * silent fallback). UI should disable the slot in that case so an invalid
 * assignment cannot proceed even with bad data.
 */
export const isEligibleForSlot = (player, slot) => {
  if (!slot || slot === 'BENCH' || slot === 'bench') return true;
  const slotType = slotTypeFromPosition(slot);
  if (!slotType) return false;
  const effective = getEffectiveEligibility(player);
  if (!effective) return false;
  return effective.includes(slotType);
};

/**
 * Returns the set of slot TYPES (e.g., 'SS', 'OF', 'UTIL') the player is
 * eligible at. Useful for rendering disabled/enabled position dropdowns.
 *
 * @param {Object} player Canonical player object
 * @returns {Set<string>} Set of eligible slot types. Empty if no data.
 */
export const eligibleSlotTypes = (player) => {
  const effective = getEffectiveEligibility(player);
  return new Set(effective || []);
};
